import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import { Cancel, CheckCircle, CloseRounded, Refresh, Visibility } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Link,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AdobeJobStatus, ApplicationStatus, ServiceType } from '@prisma/client';
import { Formik } from 'formik';
import * as yup from 'yup';

import { navigate } from '@redwoodjs/router';
import { MetaTags, useMutation, useQuery } from '@redwoodjs/web';

import { PillarHeader } from 'src/components';
import NPIButton from 'src/components/Button';
import { Flex } from 'src/components/Flex';
import { FieldInput } from 'src/components/FormikComponents';
import { OptionTypes } from 'src/components/FormikComponents/DropDown';
import Loader from 'src/components/Loader/Loader';
import { ModalBox } from 'src/components/ModalContent/Modal';
import ModalContent from 'src/components/ModalContent/ModalContent';
import ReusableGrid from 'src/components/ReusableGrid';
import StatusChip from 'src/components/StatusChip';
import { StepperComponent } from 'src/components/StepperComponent/StepperComponent';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { ADD_MERCHANT_STEPS } from 'src/constants/merchants';
import { useAuthContext } from 'src/contexts/AuthProvider';
import { COMPLETE_MERCHANT_APPLICATION } from 'src/graphql/mutations/completeMerchantApplication.mutation';
import { RESEND_AGREEMENT } from 'src/graphql/mutations/resendAgreement.mutation';
import { SUBMIT_MERCHANT_APPLICATION } from 'src/graphql/mutations/submitMerchantApplication.mutation';
import { PROCESS_MERCHANT_REQUEST_BUILD } from 'src/graphql/mutations/updateMerchant.mutation';
import { UPDATE_MERCHANT_APPLICATION_STATUS } from 'src/graphql/mutations/updateMerchantApplicationStatus.mutation';
import { GET_AGREEMENT_STATUS } from 'src/graphql/queries/getAgreementStatus.query';
import {
    GET_APPLICATION,
    GET_LATEST_ADOBE_SIGN_JOB,
} from 'src/graphql/queries/merchantApplications.query';
import { GET_MERCHANT_BY_APPLICATION_ID } from 'src/graphql/queries/merchants.query';
import { GET_NOTES } from 'src/graphql/queries/notes.query';
import { useCreateNote } from 'src/hooks';
import { PageContent } from 'src/layouts/MainLayout/PageContent';
import {
    findLastCompletedStepPosition,
    hasRequiredSupportingDocuments,
    onError,
    updateStepCompletion,
} from 'src/utils/utils';
import { AddMerchantApplicationPayload } from 'types/graphql';

import BorderedCard from '../../components/BorderedCard/BorderedCard';
import OutletInformation from '../AddMerchantPage/steps/OutletInformation';
import Notes from '../ViewPartnerPage/components/Notes/Notes';

import AuthorServiceDetails from './components/AuthorServiceDetails';
import DocumentContent from './components/DocumentContent';
import ErrorDetailsModal from './components/ErrorDetailsModal';
import SendApplicationSignatureModal from './components/SendApplicationSignatureModal';
import WorldPayInput from './components/WorldPayPO';
import {
    canApproveMerchantApplication,
    canFlagErrorIdentifiedMerchantApplication,
} from './merchantApplicationHelpers.util';
import {
    createLabeledPrincipalDocuments,
    formatDocumentOptions,
    pickSupportingDocuments,
} from './utils';

interface IMerchantApplicationPage {
    id: number;
}

const onCompleteValidationSchema = yup.object().shape({
    mid: yup.string().required('Required'),
    terminalId: yup.string().when('serviceType', (serviceType, field) => {
        return serviceType === ServiceType.TERMINAL ? field.required() : field;
    }),
});

const MerchantApplicationPage = ({ id }: IMerchantApplicationPage) => {
    const theme = useTheme();
    const { isAdmin } = useAuthContext();
    const [isUploadSupportingDocumentsModalOpen, setIsUploadSupportingDocumentsOpen] =
        useState(false);
    const [isWorldPayModalOpen, setIsWorldPayModalOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [merchant, setMerchant] = useState();
    const [isResendDisabled, setIsResendDisabled] = useState(false);
    const [worldPayButtonDisabled, setWorldPayButtonDisabled] = useState(true);
    const [hasSupportingDocuments, setHasSupportingDocuments] = useState(false);
    const [showStepperComponent, setShowStepperComponent] = useState(true);
    const [submitState, setDocumentsSubmitState] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { user } = useAuthContext();

    const [openCreateNoteModal, setOpenCreateNoteModal] = useState(false);
    const handleAddNoteModal = () => {
        setOpenCreateNoteModal(!openCreateNoteModal);
    };

    const onCaptureOnScreen = () => {
        return setIsOpen(true);
    };

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setIsOpen(false);
    };

    const { data: notes, refetch: refetchNotes } = useQuery(GET_NOTES, {
        variables: {
            input: {
                applicationId: id,
            },
        },
    });

    const { onCreateNote } = useCreateNote({
        applicationId: id,
        onRefetch: refetchNotes,
    });

    const handleCreateNote = (values: { description: string; subject: string }) => {
        onCreateNote({
            subject: values.subject,
            description: values.description,
        });
        handleAddNoteModal();
    };

    // Query used to fetch a specific merchant application
    const [merchantApplication, setMerchantApplication] =
        useState<AddMerchantApplicationPayload>(null);

    const pollInterval =
        merchantApplication?.status === ApplicationStatus.AWAITING_SIGNATURE ? 15000 : 0;

    // Fetch the merchant application
    const {
        loading: fetchingApplication,
        data,
        refetch,
    } = useQuery(GET_APPLICATION, {
        fetchPolicy: 'cache-and-network',
        variables: { id },
        pollInterval,
        onError: () => {
            showErrorToast('Error fetching the application details');
        },
        onCompleted: ({ retrieveMerchantApplication }) => {
            setMerchantApplication(retrieveMerchantApplication);
            navigate(`/merchant-applications/view-application/${retrieveMerchantApplication?.id}`);
        },
    });

    const [
        getAgreementStatus,
        {
            data: agreementStatusData,
            refetch: refetchAgreementStatus,
            loading: fetchingAgreementStatusLoading,
        },
    ] = useLazyQuery(GET_AGREEMENT_STATUS, {
        pollInterval,
        onError: () => {
            showErrorToast(`Error fetching agreement status`);
        },
    });

    const handleRefresh = useCallback(async () => {
        try {
            await refetchAgreementStatus();
            await refetch({ id });
        } catch (error) {
            showErrorToast(error.message);
        }
    }, [id, refetch, refetchAgreementStatus]);

    const { loading: fetchingMerchant, refetch: refetchMerchant } = useQuery(
        GET_MERCHANT_BY_APPLICATION_ID,
        {
            skip: !merchantApplication,
            fetchPolicy: 'cache-and-network',
            variables: { applicationId: merchantApplication?.id },
            pollInterval,
            onError: () => {
                showErrorToast('Error fetching the Merchant');
            },
            onCompleted: ({ getMerchantByApplicationId }) => {
                setMerchant(getMerchantByApplicationId);
            },
        }
    );

    const getIsCompletedValues = (payload) => {
        return {
            businessInformation:
                payload?.retrieveMerchantApplication?.businessInformation?.isStepCompleted,
            merchantInformation:
                payload?.retrieveMerchantApplication?.merchantInformation?.isStepCompleted,
            outletInformation:
                payload?.retrieveMerchantApplication?.outletInformation?.isStepCompleted,
            serviceRequirements:
                payload?.retrieveMerchantApplication?.serviceRequirements?.isStepCompleted,
            supportingDocuments:
                payload?.retrieveMerchantApplication?.supportingDocuments?.isStepCompleted,
        };
    };

    const hasOutletInformation = merchantApplication?.outletInformation;

    const OUTLET_INFO_STEP = useMemo(() => {
        return {
            label: 'Outlet Information',
            completed: false,
            accessor: 'outletInformation',
            form: <OutletInformation key="outlet-information" handleBack={() => {}} />,
        };
    }, []);

    const isCompletedValues = getIsCompletedValues(data);

    const updatedSteps = useMemo(() => {
        const updatedValues = updateStepCompletion(
            hasOutletInformation
                ? [
                      ...ADD_MERCHANT_STEPS.slice(0, 3),
                      OUTLET_INFO_STEP,
                      ...ADD_MERCHANT_STEPS.slice(3),
                  ]
                : ADD_MERCHANT_STEPS,
            isCompletedValues
        );

        return updatedValues;
    }, [OUTLET_INFO_STEP, hasOutletInformation, isCompletedValues]);

    const lastCompletedStepPosition = findLastCompletedStepPosition(updatedSteps);

    const [completeMerchantApplication, { loading: completing }] = useMutation(
        COMPLETE_MERCHANT_APPLICATION,
        {
            onError: (error) => {
                showErrorToast(`Error completing your application${error ? ` || ${error}` : ''}`);
                refetch({ id });
                refetchMerchant({ applicationId: id });
                refetchAgreementStatus?.();
            },
            onCompleted: ({ completeMerchantApplication }) => {
                setMerchantApplication(completeMerchantApplication);
                setShowStepperComponent(false);
                showSuccessToast(
                    'Your Account has been Approved and is Active! Please check your email'
                );
                refetch({ id });
                refetchMerchant({ applicationId: completeMerchantApplication.id });
                refetchAgreementStatus?.();
            },
        }
    );

    const [resendAgreement, { loading: resendingLoading }] = useMutation(RESEND_AGREEMENT, {
        onError,
        onCompleted: () => {
            showSuccessToast(
                'Agreement has been resent. Please wait 24 hours before sending another reminder.'
            );
        },
    });

    const onRefreshAll = async () => {
        refetchAgreementStatus();
        refetch();
    };

    const [applicationStatusState, setApplicationStatusState] = useState(false);

    useEffect(() => {
        if (applicationStatusState) {
            refetch().finally(() => {
                return setApplicationStatusState(false);
            });
        }
    }, [applicationStatusState, refetch]);

    const [processMerchantRequestBuild, { loading: processingRequestBuild }] = useMutation(
        PROCESS_MERCHANT_REQUEST_BUILD,
        {
            onError,
            onCompleted: () => {
                refetchMerchant({
                    applicationId: merchantApplication.id,
                });
                refetch({
                    id,
                });
                showSuccessToast('Request Build Processed.');
            },
        }
    );

    const [submitMerchantApplicationMutation, { loading: submittingMerchantApplication }] =
        useMutation(SUBMIT_MERCHANT_APPLICATION, {
            onError: (error) => {
                return showErrorToast(error.message);
            },
        });

    const [updateMerchantApplicationStatus] = useMutation(UPDATE_MERCHANT_APPLICATION_STATUS, {
        onError,
        onCompleted: () => {
            setStatusMenuOpen(false);
            onRefreshAll();
            setAnchorEl(null);
        },
    });

    useEffect(() => {
        if (data?.retrieveMerchantApplication?.id) {
            getAgreementStatus({
                variables: { applicationId: data?.retrieveMerchantApplication?.id },
            });
        }
    }, [data?.retrieveMerchantApplication?.id, getAgreementStatus]);

    const applicationId = data?.retrieveMerchantApplication?.id;

    const [fetchLatestJob, { data: latestJobData }] = useLazyQuery(GET_LATEST_ADOBE_SIGN_JOB, {
        fetchPolicy: 'network-only',
        onError,
    });

    useEffect(() => {
        if (applicationId) {
            fetchLatestJob({
                variables: { applicationId },
            });
        }
    }, [applicationId, fetchLatestJob]);

    useEffect(() => {
        if (latestJobData?.latestAdobeSignJob) {
            setIsResendDisabled(!latestJobData.latestAdobeSignJob.canResend);
        }
    }, [latestJobData]);

    const handleProcessMerchantBuild = async ({ mid, terminalId }) => {
        if (!merchantApplication?.merchant?.id) {
            await submitMerchantApplicationMutation({
                variables: { applicationId: merchantApplication?.id ?? id },
            });
        }

        processMerchantRequestBuild({
            variables: {
                input: {
                    applicationId: merchantApplication.id,
                    mid,
                    terminalId,
                },
            },
        });
    };

    const supportingDocuments: OptionTypes[] = useMemo(() => {
        const {
            retrieveMerchantApplication: { supportingDocuments: supportingDocumentList } = {},
        } = data ?? {};

        const newData: OptionTypes[] = [];
        if (supportingDocumentList) {
            const documents = pickSupportingDocuments(supportingDocumentList);

            const principalDocuments = supportingDocumentList?.principalDocuments ?? [];

            const labeledPrincipalDocuments = createLabeledPrincipalDocuments(
                principalDocuments,
                data?.retrieveMerchantApplication?.merchantInformation
            );

            newData.push(...labeledPrincipalDocuments);

            newData.push(...formatDocumentOptions(documents));
        }
        return newData;
    }, [data]);

    const isAgreementSigned = useMemo(() => {
        return agreementStatusData?.agreementStatus?.status === AdobeJobStatus.SIGNED;
    }, [agreementStatusData]);

    useEffect(() => {
        if (isAgreementSigned) {
            setApplicationStatusState(true);
        }
    }, [isAgreementSigned]);

    const { principals = [] } = data?.retrieveMerchantApplication?.merchantInformation || {};

    const { principalSignatureIndex } = data?.retrieveMerchantApplication?.adobeSignJobs?.[0] || {};

    const signer = useMemo(() => {
        const principal = principals[principalSignatureIndex];
        return {
            name: `${principal?.firstName || ''} ${principal?.lastName || ''}`,
            email: principal?.email || '',
        };
    }, [principals, principalSignatureIndex]);

    const STATUS_OPTIONS = useMemo(() => {
        return Object.values(ApplicationStatus)
            .filter((applicationStatus) => {
                return applicationStatus !== merchantApplication?.status;
            })
            .map((status) => {
                return { label: status.replace(/_/g, ' '), value: status };
            });
    }, [merchantApplication]);

    const shouldShowRequestBuildButton = useMemo(() => {
        return (
            isAdmin &&
            merchantApplication?.status === ApplicationStatus.SENT_TO_WORLDPAY_AWAITING_MID
        );
    }, [merchantApplication, isAdmin]);

    const shouldShowErrorResolvedButton = useMemo(() => {
        return merchantApplication?.status === ApplicationStatus.ERROR_IDENTIFIED;
    }, [merchantApplication]);

    useEffect(() => {
        const businessType =
            merchantApplication?.merchantInformation?.aboutBusiness?.businessType ?? '';

        const settlementAccountSameAsChargeable =
            merchantApplication?.businessInformation?.settlementAccountSameAsChargeable ?? '';

        const applicationSupportingDocuments = merchantApplication?.supportingDocuments;

        const result = hasRequiredSupportingDocuments(
            businessType,
            settlementAccountSameAsChargeable,
            applicationSupportingDocuments
        );

        setHasSupportingDocuments(result);
    }, [merchantApplication]);

    const handleWorldPaySend = () => {
        if (!hasSupportingDocuments && !submitState) {
            setIsUploadSupportingDocumentsOpen(true);
        } else {
            setIsWorldPayModalOpen(true);
        }
    };

    const onResendAgreement = async () => {
        await resendAgreement({ variables: { applicationId: id } });
        setIsResendDisabled(true);
    };

    // #region Application Status Update
    const onApplicationStatusUpdate = async (status: ApplicationStatus) => {
        try {
            await updateMerchantApplicationStatus({
                variables:
                    status === ApplicationStatus.ACTIVE
                        ? {
                              status,
                              applicationId: id,
                          }
                        : { status, applicationId: id },
            });

            showSuccessToast('Application Status Updated!');
            setApplicationStatusState(true);

            if (merchantApplication?.status === ApplicationStatus.ERROR_IDENTIFIED) {
                getActiveStep(1);
            }
        } catch (error) {
            showErrorToast('Failed to update application status');
        }
    };
    // #endregion

    const merchantAccountNumber = merchantApplication?.merchantAccountNumber;

    const handleContinueClick = () => {
        // Ensure activeStep is within a valid range (0 to ADD_MERCHANT_STEPS.length - 1)
        // navigate(`/merchant/add?isDraftApplication=${merchantApplication?.id}?step=${2}`)
        navigate(`/merchant/add?merchantApplicationId=${merchantApplication?.id}`);
    };

    const getActiveStep = (e: number): void => {
        navigate(`/merchant/add?merchantApplicationId=${merchantApplication?.id}&step=${e}`);
    };
    const handleApprove = async () => {
        if (merchantApplication?.status === ApplicationStatus.APPLICATION_ACCEPTED) {
            await completeMerchantApplication({
                variables: {
                    input: {
                        applicationId: id,
                        merchantAccountNumber,
                    },
                },
            });
        } else if (
            merchantApplication?.status !== ApplicationStatus.SENT_TO_WORLDPAY_AWAITING_MID
        ) {
            showErrorToast('This application has not yet been sent off to WorldPay');
        }
    };

    const merchantTradingName = merchantApplication?.tradingName ?? '';

    const merchantLegalName =
        merchantApplication?.merchantInformation?.aboutBusiness?.legalCompanyName ?? '';

    const merchantLegalNamePlusTradingName = `${merchantLegalName} T/A ${merchantTradingName}`;

    useEffect(() => {
        switch (merchantApplication?.status) {
            case ApplicationStatus.NPI_INTERNAL_CS_REVIEW:
                setWorldPayButtonDisabled(false);
                break;
            case ApplicationStatus.SENT_TO_WORLDPAY_AWAITING_MID:
                setWorldPayButtonDisabled(true);
                break;
            default:
                break;
        }
    }, [hasSupportingDocuments, merchantApplication, supportingDocuments]);

    const showManageSignature =
        merchantApplication?.status === ApplicationStatus.NPI_INTERNAL_CS_REVIEW ||
        merchantApplication?.status === ApplicationStatus.AWAITING_SIGNATURE ||
        merchantApplication?.status === ApplicationStatus.AWAITING_SUPPORTING_DOCUMENTS;

    const isApproving = completing;

    return (
        <PageContent>
            <MetaTags title="MerchantApplication" description="MerchantApplication page" />
            {fetchingApplication || fetchingMerchant || completing ? <Loader /> : null}
            <Formik
                initialValues={{
                    mid: merchantApplication?.mid || '',
                    terminalId: merchantApplication?.terminalId || '',
                    serviceType:
                        merchantApplication?.serviceRequirements?.service?.serviceType || '',
                }}
                onSubmit={async ({ mid, terminalId }) => {
                    handleProcessMerchantBuild({ mid, terminalId });
                }}
                enableReinitialize
                validationSchema={onCompleteValidationSchema}
            >
                {({ values, handleSubmit }) => {
                    const { mid, terminalId } = values;
                    return (
                        <>
                            {' '}
                            {!fetchingApplication && !fetchingMerchant ? (
                                <>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            width: '100%',
                                            gap: '24px',
                                        }}
                                    >
                                        {showStepperComponent && (
                                            <StepperComponent
                                                stepsList={updatedSteps}
                                                activeStep={lastCompletedStepPosition}
                                                showStepLabel={false}
                                                setActiveStep={getActiveStep}
                                            />
                                        )}
                                    </Box>
                                    <PillarHeader
                                        heading={merchantLegalNamePlusTradingName}
                                        subheading={merchantAccountNumber}
                                    />
                                    <AuthorServiceDetails application={merchantApplication} />

                                    <BorderedCard flexDirection="row">
                                        <Flex alignItems="center">
                                            <Typography
                                                fontWeight="500"
                                                fontSize="24px"
                                                lineHeight="34px"
                                                sx={{ color: 'text.primary' }}
                                                marginRight="16px"
                                            >
                                                Application Status:
                                            </Typography>
                                            <StatusChip
                                                status={(merchantApplication?.status ?? '').replace(
                                                    /_/g,
                                                    ' '
                                                )}
                                            />
                                        </Flex>

                                        <SendApplicationSignatureModal
                                            isModalOpen={isOpen}
                                            handleCloseModal={handleCloseModal}
                                            application={merchantApplication}
                                        />
                                        {merchantApplication?.status === ApplicationStatus.DRAFT ? (
                                            <Button
                                                variant="npi-primary"
                                                onClick={handleContinueClick}
                                            >
                                                continue application
                                            </Button>
                                        ) : (
                                            <Flex gap="20px">
                                                {merchantApplication?.status ===
                                                    ApplicationStatus.EXPIRED && (
                                                    <Button
                                                        variant="npi-primary"
                                                        onClick={onCaptureOnScreen}
                                                    >
                                                        Resend for Signature
                                                    </Button>
                                                )}
                                                <Link
                                                    href={
                                                        agreementStatusData?.agreementStatus
                                                            ?.previewUrl
                                                    }
                                                    target="_blank"
                                                >
                                                    <Button
                                                        startIcon={<Visibility />}
                                                        variant="npi-primary"
                                                    >
                                                        View
                                                    </Button>
                                                </Link>
                                                {(() => {
                                                    if (
                                                        canApproveMerchantApplication(
                                                            isAdmin,
                                                            merchantApplication,
                                                            merchant
                                                        )
                                                    ) {
                                                        return (
                                                            <LoadingButton
                                                                startIcon={<CheckCircle />}
                                                                variant="npi-primary"
                                                                onClick={handleApprove}
                                                                loading={isApproving}
                                                                disabled={
                                                                    merchantApplication?.status !==
                                                                    ApplicationStatus.APPLICATION_ACCEPTED
                                                                }
                                                            >
                                                                Approve
                                                            </LoadingButton>
                                                        );
                                                    }

                                                    if (shouldShowRequestBuildButton) {
                                                        return (
                                                            <LoadingButton
                                                                startIcon={<CheckCircle />}
                                                                variant="npi-primary"
                                                                type="button"
                                                                sx={{
                                                                    color: 'white',
                                                                }}
                                                                loading={
                                                                    processingRequestBuild ||
                                                                    submittingMerchantApplication
                                                                }
                                                                disabled={
                                                                    merchantApplication
                                                                        .serviceRequirements
                                                                        ?.service?.serviceType ===
                                                                    ServiceType.TERMINAL
                                                                        ? !(mid && terminalId)
                                                                        : !mid
                                                                }
                                                                onClick={() => {
                                                                    return handleSubmit();
                                                                }}
                                                            >
                                                                Request Build
                                                            </LoadingButton>
                                                        );
                                                    }

                                                    if (shouldShowErrorResolvedButton) {
                                                        return (
                                                            <Button
                                                                startIcon={<CloseRounded />}
                                                                variant="npi-primary"
                                                                type="button"
                                                                sx={{
                                                                    color: 'white',
                                                                }}
                                                                onClick={() => {
                                                                    return onApplicationStatusUpdate(
                                                                        ApplicationStatus.DRAFT
                                                                    );
                                                                }}
                                                            >
                                                                Resolve Errors Now
                                                            </Button>
                                                        );
                                                    }
                                                })()}
                                                <Menu
                                                    id="basic-menu"
                                                    anchorEl={anchorEl}
                                                    open={statusMenuOpen}
                                                    onClose={() => {
                                                        return setStatusMenuOpen(false);
                                                    }}
                                                    MenuListProps={{
                                                        'aria-labelledby': 'basic-button',
                                                    }}
                                                >
                                                    {STATUS_OPTIONS.map(({ value, label }) => {
                                                        return (
                                                            <MenuItem
                                                                key={value}
                                                                onClick={() => {
                                                                    return onApplicationStatusUpdate(
                                                                        value
                                                                    );
                                                                }}
                                                            >
                                                                {label}
                                                            </MenuItem>
                                                        );
                                                    })}
                                                </Menu>
                                                <NPIButton
                                                    hidden={
                                                        !canFlagErrorIdentifiedMerchantApplication(
                                                            isAdmin,
                                                            merchantApplication,
                                                            user
                                                        )
                                                    }
                                                    startIcon={<CloseRounded />}
                                                    onClick={
                                                        user.id !== merchantApplication?.authorId
                                                            ? handleOpenModal
                                                            : () => {
                                                                  return onApplicationStatusUpdate(
                                                                      'ERROR_IDENTIFIED'
                                                                  );
                                                              }
                                                    }
                                                    label="Error Identified"
                                                />
                                                <ErrorDetailsModal
                                                    isModalOpen={isModalOpen}
                                                    handleCloseModal={handleCloseModal}
                                                    merchantApplication={merchantApplication}
                                                    onApplicationStatusUpdate={
                                                        onApplicationStatusUpdate
                                                    }
                                                />
                                            </Flex>
                                        )}
                                    </BorderedCard>
                                    {/* Row 2 */}
                                    <Flex width="100%" gap="24px">
                                        {showManageSignature ? (
                                            <BorderedCard
                                                flex={1}
                                                flexDirection="column"
                                                height={'200px'}
                                            >
                                                <Typography
                                                    fontWeight="500"
                                                    fontSize="24px"
                                                    lineHeight="34px"
                                                    color={theme.palette.text.primary}
                                                    marginRight="16px"
                                                >
                                                    Manage Signature
                                                </Typography>

                                                <Typography
                                                    fontWeight="500"
                                                    fontSize="14px"
                                                    lineHeight="20px"
                                                    color={theme.palette.text.primary}
                                                    marginTop="36px"
                                                    marginBottom={signer ? '24px' : '0px'}
                                                >
                                                    Signer
                                                </Typography>
                                                <Flex flexDirection="column" gap="16px">
                                                    <Flex
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                    >
                                                        <Flex alignItems="center">
                                                            <Typography
                                                                fontWeight="500"
                                                                fontSize="14px"
                                                                lineHeight="20px"
                                                                color={theme.palette.text[400]}
                                                                marginRight="16px"
                                                            >
                                                                {signer?.name}
                                                            </Typography>
                                                            <Typography
                                                                fontWeight="500"
                                                                fontSize="14px"
                                                                lineHeight="20px"
                                                                color={theme.palette.text[200]}
                                                            >
                                                                {signer?.email}
                                                            </Typography>
                                                        </Flex>
                                                        {merchantApplication?.status !==
                                                            ApplicationStatus.EXPIRED && (
                                                            <Flex alignItems="center" gap="8px">
                                                                {isAgreementSigned ? (
                                                                    <CheckCircle
                                                                        sx={{
                                                                            color: theme.palette
                                                                                .primary.main,
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Cancel
                                                                        sx={{
                                                                            color: theme.palette
                                                                                .primary.main,
                                                                        }}
                                                                    />
                                                                )}
                                                                {!isAgreementSigned && (
                                                                    <LoadingButton
                                                                        onClick={handleRefresh}
                                                                        loading={
                                                                            fetchingApplication &&
                                                                            fetchingAgreementStatusLoading
                                                                        }
                                                                        loadingPosition="start"
                                                                        startIcon={<Refresh />}
                                                                        sx={{
                                                                            color:
                                                                                fetchingApplication &&
                                                                                fetchingAgreementStatusLoading
                                                                                    ? theme.palette
                                                                                          .grey.A400
                                                                                    : theme.palette
                                                                                          .primary
                                                                                          .main,
                                                                            minWidth: 'auto',
                                                                            padding: '6px',
                                                                            '& .MuiSvgIcon-root': {
                                                                                fontSize: '1.5rem',
                                                                            },
                                                                        }}
                                                                    />
                                                                )}
                                                                {!isAgreementSigned && (
                                                                    <LoadingButton
                                                                        variant="npi-primary"
                                                                        onClick={onResendAgreement}
                                                                        loading={resendingLoading}
                                                                        disabled={isResendDisabled}
                                                                        sx={{
                                                                            minWidth: 'auto',
                                                                            padding: '6px',
                                                                        }}
                                                                    >
                                                                        Resend
                                                                    </LoadingButton>
                                                                )}
                                                            </Flex>
                                                        )}
                                                    </Flex>
                                                </Flex>
                                            </BorderedCard>
                                        ) : null}

                                        <ReusableGrid
                                            title="Notes"
                                            addRecordButtonConfig={{
                                                addRecordButtonText: 'Add Note',
                                                addRecordCallBack: handleAddNoteModal,
                                            }}
                                            enableContainer={showManageSignature}
                                        >
                                            <Notes
                                                notes={notes?.notes}
                                                refetch={refetchNotes}
                                                maxHeight="400px"
                                            />
                                            <Dialog
                                                open={openCreateNoteModal}
                                                onClose={handleAddNoteModal}
                                                aria-labelledby="create-note-offer-dialog"
                                                sx={{
                                                    '& .MuiDialog-paper': {
                                                        width: '400px',
                                                    },
                                                }}
                                            >
                                                <DialogContent>
                                                    <ModalContent
                                                        handleClose={handleAddNoteModal}
                                                        buttonText="Create Note"
                                                        header="Create Note"
                                                        initialValues={{
                                                            description: '',
                                                            subject: '',
                                                        }}
                                                        handleFormSubmit={handleCreateNote}
                                                        isSubmitModal
                                                        handleAction={() => {}}
                                                        message=""
                                                    />
                                                </DialogContent>
                                            </Dialog>
                                        </ReusableGrid>
                                    </Flex>
                                    {/* Row 3 */}
                                    {(merchantApplication?.status ===
                                        ApplicationStatus.SENT_TO_WORLDPAY_AWAITING_MID ||
                                        merchantApplication?.status ===
                                            ApplicationStatus.APPLICATION_ACCEPTED ||
                                        merchantApplication?.status === ApplicationStatus.ACTIVE) &&
                                        (merchantApplication?.isServiceApplication
                                            ? true
                                            : merchantApplication?.sentToWorldpayAt !== null) && (
                                            <Flex width="100%" gap="24px">
                                                {isAdmin &&
                                                    merchantApplication?.status !==
                                                        ApplicationStatus.ACTIVE &&
                                                    merchantApplication?.status !==
                                                        ApplicationStatus.APPLICATION_ACCEPTED && (
                                                        <WorldPayInput
                                                            existingWorldpayPO={
                                                                merchantApplication?.worldpayPO
                                                            }
                                                            merchantApplicationId={
                                                                merchantApplication.id
                                                            }
                                                        />
                                                    )}

                                                <BorderedCard
                                                    width={isAdmin ? '100%' : '50%'}
                                                    gap="8px"
                                                    flexDirection="column"
                                                >
                                                    <Typography
                                                        fontWeight="500"
                                                        fontSize="24px"
                                                        lineHeight="34px"
                                                        color="#1D1D1B"
                                                        marginBottom="10px"
                                                    >
                                                        Complete Application (Add MID)
                                                    </Typography>
                                                    <FieldInput
                                                        label="Merchant ID (MID)"
                                                        name="mid"
                                                    />
                                                    {values.serviceType !== ServiceType.ONLINE && (
                                                        <FieldInput label="TID" name="terminalId" />
                                                    )}
                                                </BorderedCard>
                                            </Flex>
                                        )}
                                    {/* Row 4 */}
                                    <Flex justifyContent="space-between" gap="24px">
                                        <BorderedCard flex={1}>
                                            <Flex
                                                alignItems="center"
                                                justifyContent="space-between"
                                            >
                                                <Typography
                                                    fontWeight="500"
                                                    fontSize="24px"
                                                    lineHeight="34px"
                                                    color="#1D1D1B"
                                                >
                                                    History
                                                </Typography>
                                            </Flex>
                                        </BorderedCard>

                                        {isAdmin && (
                                            <Button
                                                variant="npi-primary"
                                                disabled={worldPayButtonDisabled}
                                                onClick={handleWorldPaySend}
                                            >
                                                Send to WorldPay
                                            </Button>
                                        )}
                                    </Flex>
                                    <ModalBox
                                        open={
                                            isWorldPayModalOpen ||
                                            isUploadSupportingDocumentsModalOpen
                                        }
                                        handleCloseModal={() => {
                                            setIsUploadSupportingDocumentsOpen(false);
                                            setIsWorldPayModalOpen(false);
                                        }}
                                        title={
                                            isUploadSupportingDocumentsModalOpen
                                                ? 'Upload Documents'
                                                : 'Send to WorldPay'
                                        }
                                        widthSize="1080px"
                                    >
                                        <DocumentContent
                                            isUploadSupportingDocumentsModalOpen={
                                                isUploadSupportingDocumentsModalOpen
                                            }
                                            merchantApplication={merchantApplication}
                                            id={id}
                                            supportingDocuments={supportingDocuments}
                                            setIsUploadSupportingDocumentsOpen={
                                                setIsUploadSupportingDocumentsOpen
                                            }
                                            setIsWorldPayModalOpen={setIsWorldPayModalOpen}
                                            refetch={refetch}
                                            setDocumentsSubmitState={setDocumentsSubmitState}
                                        />
                                    </ModalBox>
                                </>
                            ) : null}
                        </>
                    );
                }}
            </Formik>
        </PageContent>
    );
};

export default MerchantApplicationPage;
